/**
 * Client API cho module BLD Reports (báo cáo Ban lãnh đạo).
 *
 * Server đã chuẩn hoá sẵn cả camelCase (response) và snake_case (request body):
 * - GET  /api/bld-reports/config → { config, issues, warnings, outlook }
 * - PUT  /api/bld-reports/config ← { enabled, schedule_cron, sharepoint_urls,
 *                                    teams_private_channel, teams_send_enabled }
 *
 * Không lưu secret ở client; chỉ hiển thị trạng thái env do server trả về.
 *
 * Mọi hàm nhận `apiBaseUrl` (API base runtime — prop `apiBaseUrl` khi S3 Explorer được
 * nhúng vào frontend host, vd `https://dev-hoaphat.cagent.cmcts.ai/s3-explorer`) và ghép
 * URL tuyệt đối, KHÔNG dựa vào `axios.defaults.baseURL`: bundle đã build của package không
 * biết domain triển khai nên mặc định sẽ rơi về `http://localhost:3000` (xem `lib/config.ts`).
 */

import axios from './axios';

/**
 * Thư mục S3 chứa artifact báo cáo BLD (`bld-reports/<periodType>/<periodKey>/report.{html,png}`
 * — xem `packages/server/src/modules/bld-reports/reports/artifacts.ts`).
 *
 * Trên S3 Explorer, đây là thư mục DUY NHẤT hiển thị nút cấu hình scheduler và nút
 * "Lịch sử báo cáo" (trước đây gắn nhầm vào `kho_ban_lanh_dao/`).
 */
const BLD_REPORTS_FOLDER_SEGMENT = 'bld-reports';
export const BLD_REPORTS_FOLDER_PREFIX = `${BLD_REPORTS_FOLDER_SEGMENT}/`;

/** True khi prefix hiện tại là chính thư mục `bld-reports` (không áp dụng cho thư mục con). */
export function isBldReportsFolderPrefix(prefix: string | null | undefined): boolean {
  if (!prefix) return false;
  // Prefix có thể có `/` ở đầu (root) và luôn có `/` ở cuối; so khớp sau khi chuẩn hoá.
  return prefix.replace(/^\/+|\/+$/g, '') === BLD_REPORTS_FOLDER_SEGMENT;
}

/** 4 nguồn dữ liệu Excel đọc từ SharePoint. */
export type BldSourceKey =
  | 'production_inventory'
  | 'electrical_structure'
  | 'equipment_status'
  | 'production_stoppage';

export const BLD_SOURCE_KEYS: readonly BldSourceKey[] = [
  'production_inventory',
  'electrical_structure',
  'equipment_status',
  'production_stoppage',
];

/** Nhãn tiếng Việt của 4 nguồn (dùng cho UI). */
export const BLD_SOURCE_LABELS: Record<BldSourceKey, string> = {
  production_inventory: 'Tồn kho sản xuất (sản xuất – bán hàng – tồn kho)',
  electrical_structure: 'Cơ cấu điện',
  equipment_status: 'Tình trạng thiết bị hàng ngày',
  production_stoppage: 'Kế hoạch dừng sản xuất',
};

export type BldSharePointUrls = Partial<Record<BldSourceKey, string>>;

export interface BldTeamsPrivateChannel {
  teamId: string;
  channelId: string;
}

export interface BldReportsConfig {
  enabled: boolean;
  scheduleCron: string;
  sharepointUrls: BldSharePointUrls;
  teamsPrivateChannel: BldTeamsPrivateChannel | null;
  teamsSendEnabled: boolean;
  /** Hiện thông tin chẩn đoán (chip nguồn/ngày dữ liệu, cảnh báo/lỗi) trong HTML + card Teams. */
  showDevWarnings: boolean;
  /** Cho phép ghi đè dữ liệu ngày đã tồn tại (đã báo cáo) — mặc định false (khóa, không sửa). */
  allowOverwriteExistingDays: boolean;
  /** Mailbox Ban lãnh đạo cho 2 API app-only của CEO Assistant/MCP (unread-messages, calendar). */
  mailbox: string | null;
}

export interface BldConfigIssue {
  key: string;
  message: string;
}

export interface BldReportsConfigSnapshot {
  config: BldReportsConfig;
  issues: BldConfigIssue[];
  warnings: string[];
  /** Trạng thái cấu hình app-only phía server (chỉ tên env thiếu, không giá trị). */
  outlook: { configured: boolean; missingVars: string[] };
}

/** True nếu chuỗi là URL https thuộc tenant SharePoint (*.sharepoint.com). */
export function isSharePointUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && url.hostname.toLowerCase().endsWith('.sharepoint.com');
  } catch {
    return false;
  }
}

/** True nếu chuỗi là địa chỉ email hợp lệ ở mức cơ bản (dùng cho mailbox Ban lãnh đạo). */
export function isMailboxAddress(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());
}

/**
 * Parse URL channel private của Teams thành { teamId, channelId }.
 * Hỗ trợ các dạng:
 * - https://teams.microsoft.com/l/team/<teamId>/conversations?groupId=<teamId>&tenantId=...
 * - https://teams.microsoft.com/l/channel/<channelId>/<name>?groupId=<teamId>
 * Trả về null nếu thiếu 1 trong 2 định danh.
 */
export function parseTeamsChannelUrl(value: string): BldTeamsPrivateChannel | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }

  const segments = url.pathname.split('/').filter(Boolean);
  let teamId: string | null = null;
  let channelId: string | null = null;
  try {
    for (let index = 0; index < segments.length - 1; index += 1) {
      const marker = segments[index]?.toLowerCase();
      const next = segments[index + 1];
      if (!next) continue;
      if (marker === 'team' && !teamId) teamId = decodeURIComponent(next);
      if (marker === 'channel' && !channelId) channelId = decodeURIComponent(next);
    }
  } catch {
    return null;
  }

  const groupId = url.searchParams.get('groupId');
  if (!teamId && groupId) teamId = groupId;
  if (!teamId || !channelId) return null;
  return { teamId, channelId };
}

/** Body PUT /api/bld-reports/config (đúng tên khoá snake_case server nhận). */
export interface SaveBldReportsConfigInput {
  enabled: boolean;
  schedule_cron: string;
  sharepoint_urls: BldSharePointUrls;
  teams_private_channel: BldTeamsPrivateChannel | null;
  teams_send_enabled: boolean;
  show_dev_warnings: boolean;
  allow_overwrite_existing_days: boolean;
  mailbox: string | null;
}

function extractError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    const data = err.response.data as {
      error?: string;
      message?: string;
      issues?: BldConfigIssue[];
    };
    const firstIssue = Array.isArray(data.issues)
      ? data.issues.find(
          (issue) => issue && typeof issue.message === 'string' && issue.message.trim().length > 0,
        )
      : undefined;
    if (firstIssue) return data.error ? `${data.error}: ${firstIssue.message}` : firstIssue.message;
    if (data.error) return data.error;
    if (data.message) return data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** GET /api/bld-reports/config — đọc cấu hình (apiBaseUrl: API base runtime của caller). */
export async function getBldReportsConfig(apiBaseUrl: string): Promise<BldReportsConfigSnapshot> {
  const response = await axios.get<BldReportsConfigSnapshot>(
    `${apiBaseUrl}/api/bld-reports/config`,
  );
  return response.data;
}

export async function saveBldReportsConfig(
  apiBaseUrl: string,
  input: SaveBldReportsConfigInput,
): Promise<BldReportsConfig> {
  try {
    const response = await axios.put<{ ok: boolean; config: BldReportsConfig }>(
      `${apiBaseUrl}/api/bld-reports/config`,
      input,
    );
    return response.data.config;
  } catch (err) {
    throw new Error(extractError(err, 'Không lưu được cấu hình báo cáo Ban lãnh đạo'));
  }
}
